import React from 'react';
import { isEqual } from 'lodash';
import memoize from 'memoize-async';
import { ApLoaderDefault } from '@alixpartners/ui-components';

import ChangeTypeModal from 'Details/ChangeTypeModal/ChangeTypeModal';
import TopVendorsModal from 'Details/TopVendorsModal';
import MoveToBinModal from 'Details/MoveToBinModal/MoveToBinModal';
import { CHART_TYPES } from 'Details/DetailsChart/DetailsChart';
import { HeroChartOptionsContainer } from '../HeroChartOptions';
import HeroChart from '../HeroChart';
import DownloadProgressBar from 'Data/DownloadData/DownloadProgressBar';
import { getCategoryLevelFromBreadcrumbs, getDiagonalPattern } from './utils';
import { TAIL_SPEND } from 'utils/detailsChartDataConverter';
import { safeSum } from 'Pmi/utils';
import { INDIRECT, DIRECT } from 'Pmi/constants';
import { getHierarchyById, CHART_ITEMS_LIMIT } from 'Details/utils';
import { SPEND_PARAM } from 'Data/constants';
import {
	getWaterfallChartDataFromWheel,
	generateHeroChartDataFile,
} from 'utils/apiRequests';
import {
	getCompaniesWithDefaults,
	getHeroChartFileExportParamsFromProps,
	getHeroChartParamsFromProps,
} from 'Pmi/HeroChart/utils';
import { CHART_COLORS } from 'styles/colors';
import NoData from '../NoData';
import { PmiContext } from '../context';

import {
	Wrapper,
	FluidSection,
	LoadingContainer,
	NoDataWrapper,
} from './styles';

const memoizedApi = {
	getWaterfallChartDataFromWheel: memoize(getWaterfallChartDataFromWheel),
	generateHeroChartDataFile: memoize(generateHeroChartDataFile),
};

export class HeroChartContainer extends React.Component {
	state = {
		changeStatusModalData: null,
		topVendorsModalData: null,
		moveToBinModalData: null,
		chartMinMax: {},
		isLoading: true,
	};

	static contextType = PmiContext;

	async componentDidMount() {
		await this.fetchData();
	}

	fetchData = async () => {
		if (!this.props.companies || this.props.companies.length === 0) {
			return;
		}

		const params = getHeroChartParamsFromProps(this.props);
		const api = this.props.memoizedApi || memoizedApi;

		const waiter = api.getWaiterFor
			? await api.getWaiterFor('wheelData')
			: null;

		const rawChartData = await api.getWaterfallChartDataFromWheel(params);
		waiter && waiter.done();

		const chartData = this.getProcessedChartData(rawChartData, this.props);

		this.updateNoDataStateForAllCharts(chartData.noData);

		this.setState({
			chartData,
			rawChartData,
			isLoading: false,
		});
	};

	componentDidUpdate(prevProps) {
		this.fetchDataIfNeeded(prevProps);
	}

	fetchDataIfNeeded(prevProps) {
		const currentParams = getHeroChartParamsFromProps(this.props);
		const prevParams = getHeroChartParamsFromProps(prevProps);

		if (
			!isEqual(prevParams, currentParams) ||
			!isEqual(prevProps.companies, this.props.companies) ||
			prevProps.chartType !== this.props.chartType ||
			prevProps.pmiBreadcrumbs !== this.props.pmiBreadcrumbs
		) {
			this.fetchData();
		}
	}

	updateNoDataStateForAllCharts(noData) {
		const { updateNoDataStateForAllCharts } = this.props;

		updateNoDataStateForAllCharts(noData);
	}

	getDataFilteredBySelectedCompaniesValues(data, companies) {
		return data.filter(item => {
			const itemValue = companies
				.map(companyName => item.splitByDimension[companyName] || 0)
				.reduce(safeSum, 0);

			return itemValue;
		});
	}

	getDataCategories(rawChartData, props) {
		const {
			addressable: addressableData,
			nonAddressable: nonAddressableData,
			categoryName,
		} = rawChartData;
		let {
			chartType,
			showNonSourceable,
			pmiBreadcrumbs,
			companies,
			companiesList,
		} = props;

		let data = showNonSourceable ? nonAddressableData : addressableData;
		companies = getCompaniesWithDefaults(companies, companiesList);
		data = this.getDataFilteredBySelectedCompaniesValues(data, companies);

		let categories = data.map(item => item.name);

		if (chartType === CHART_TYPES.waterfall) {
			const categoryLevel = getCategoryLevelFromBreadcrumbs(pmiBreadcrumbs);
			const needToAddAddressableNonAddressable =
				chartType === CHART_TYPES.waterfall && categoryLevel === 0;

			categories.reverse();

			categories.push('');

			if (needToAddAddressableNonAddressable) {
				const [primaryTotalTitle, secondaryTotalTitle] = showNonSourceable
					? ['Non <br/>Sourceable', 'Sourceable']
					: ['Sourceable', 'Non <br/>Sourceable'];
				categories.push(primaryTotalTitle, secondaryTotalTitle);
			} else {
				const currentCategory = categoryName;

				categories.push(currentCategory);
			}
		}

		return categories;
	}

	getDataTotalsPerCompany(data, companies) {
		const companiesNumber = companies.length || 1;
		const totalsPerCompany = Array(companiesNumber).fill(0);

		companies.forEach((companyName, index) => {
			data.forEach(item => {
				const value = item.splitByDimension[companyName] || 0;
				totalsPerCompany[index] = totalsPerCompany[index]
					? totalsPerCompany[index] + value
					: value;
			});
		});

		return totalsPerCompany;
	}

	getDataSeries(rawChartData, props) {
		const {
			addressable: addressableData,
			nonAddressable: nonAddressableData,
		} = rawChartData;
		let {
			chartType,
			companies,
			showTailSpend,
			showNonSourceable,
			companiesList,
			pmiBreadcrumbs,
			isTailSpend,
		} = props;

		let data = showNonSourceable ? nonAddressableData : addressableData;
		companies = getCompaniesWithDefaults(companies, companiesList);
		data = this.getDataFilteredBySelectedCompaniesValues(data, companies);

		const dataContainsTailSpendBar = this.getIfDataContainsTailSpendBar(data);

		const series = companies.map(companyName => ({
			data: (chartType === CHART_TYPES.waterfall
				? [...data].reverse()
				: [...data]
			).map(item => {
				const value = item.splitByDimension[companyName] || 0;

				return value;
			}),
			stack: 0,
			name: companyName,
			maxPointWidth: 256,
		}));

		if (chartType === CHART_TYPES.bar) {
			if (showTailSpend) {
				// set tail spend colors
				if (isTailSpend) {
					series.forEach((s, index) => {
						s.data = s.data.map(value => ({
							value,
							color: getDiagonalPattern(CHART_COLORS[index]),
						}));
					});
				} else if (dataContainsTailSpendBar) {
					series.forEach((s, index) => {
						const lastIndex = s.data.length - 1;
						s.data[lastIndex] = {
							value: s.data[lastIndex],
							color: getDiagonalPattern(CHART_COLORS[index]),
						};
					});
				}
			}
		}

		if (chartType === CHART_TYPES.waterfall) {
			const categoryLevel = getCategoryLevelFromBreadcrumbs(pmiBreadcrumbs);
			const needToAddAddressableNonAddressable =
				chartType === CHART_TYPES.waterfall && categoryLevel === 0;

			if (showTailSpend) {
				// set tail spend colors
				if (isTailSpend) {
					series.forEach((s, index) => {
						s.data = s.data.map(value => ({
							value,
							color: getDiagonalPattern(CHART_COLORS[index]),
						}));
					});
				} else if (dataContainsTailSpendBar) {
					series.forEach((s, index) => {
						s.data[0] = {
							value: s.data[0],
							color: getDiagonalPattern(CHART_COLORS[index]),
						};
					});
				}
			}
			const totalsPerCompany = this.getDataTotalsPerCompany(data, companies);
			const total = totalsPerCompany.reduce((a, b) => a + b);
			// add total bar (will be hidden)
			series.forEach(
				(s, index) => (index === 0 ? s.data.push(-total) : s.data.push(0))
			);

			// add addressable
			series.forEach((s, index) =>
				s.data.push(
					isTailSpend
						? {
								value: totalsPerCompany[index],
								color: getDiagonalPattern(CHART_COLORS[index]),
						  }
						: totalsPerCompany[index]
				)
			);

			if (needToAddAddressableNonAddressable) {
				// push non-sourceable
				const secondaryData = showNonSourceable
					? addressableData
					: nonAddressableData;
				const secondaryTotalsPerCompany = this.getDataTotalsPerCompany(
					secondaryData,
					companies
				);

				series.forEach((s, index) =>
					s.data.push(
						isTailSpend
							? {
									value: secondaryTotalsPerCompany[index],
									color: getDiagonalPattern(CHART_COLORS[index]),
							  }
							: secondaryTotalsPerCompany[index]
					)
				);
			}
		}

		return series;
	}

	getPointsColorsForSeries(rawChartData, props) {
		const {
			addressable: addressableData,
			nonAddressable: nonAddressableData,
		} = rawChartData;
		let { showNonSourceable, companiesList, companies } = props;

		let data = showNonSourceable ? nonAddressableData : addressableData;
		companies = getCompaniesWithDefaults(companies, companiesList);
		data = this.getDataFilteredBySelectedCompaniesValues(data, companies);
		data = data.length > 0 ? data : Array(CHART_ITEMS_LIMIT).fill(0);

		return CHART_COLORS.map(color => data.map(_0 => color));
	}

	getProcessedChartData(rawChartData, props) {
		let { chartType, currency } = props;

		if (this.isEmptyData(rawChartData, props)) {
			return {
				noData: true,
			};
		}

		const categories = this.getDataCategories(rawChartData, props);

		let result = {
			categories,
			currency,
			type: chartType,
			series: this.getDataSeries(rawChartData, props),
			pointsColorsForSeries: this.getPointsColorsForSeries(rawChartData, props),
			highlightItemIndex: this.getHighlightChartItemIndex(categories),
			grouppedItemIndex: this.getGrouppedItemIndex(rawChartData, props),
			tailSpendItemIndex: this.getTailSpendIndex(rawChartData, props),
		};

		return result;
	}

	getGrouppedItemIndex(rawChartData, props) {
		const {
			addressable: addressableData,
			nonAddressable: nonAddressableData,
		} = rawChartData;
		let {
			showNonSourceable,
			companies,
			companiesList,
			chartType,
			pmiBreadcrumbs,
		} = props;

		const categoryLevel = getCategoryLevelFromBreadcrumbs(pmiBreadcrumbs);

		if (chartType === CHART_TYPES.waterfall && categoryLevel === 0) {
			return [0, 1];
		}

		const data = showNonSourceable ? nonAddressableData : addressableData;
		companies = getCompaniesWithDefaults(companies, companiesList);

		const filteredData = this.getDataFilteredBySelectedCompaniesValues(
			data,
			companies
		);

		let result = filteredData.findIndex(({ isGrouped }) => isGrouped);

		if (chartType === CHART_TYPES.waterfall) {
			result = result === -1 ? result : result + 2;
		}

		return result;
	}

	getTailSpendIndex(rawChartData, props) {
		const {
			addressable: addressableData,
			nonAddressable: nonAddressableData,
		} = rawChartData;
		let {
			showNonSourceable,
			companies,
			companiesList,
			chartType,
			pmiBreadcrumbs,
		} = props;

		const categoryLevel = getCategoryLevelFromBreadcrumbs(pmiBreadcrumbs);
		const data = showNonSourceable ? nonAddressableData : addressableData;
		companies = getCompaniesWithDefaults(companies, companiesList);

		const filteredData = this.getDataFilteredBySelectedCompaniesValues(
			data,
			companies
		);

		let result = filteredData.findIndex(({ isTailSpend }) => !!isTailSpend);

		if (chartType === CHART_TYPES.waterfall) {
			const offset = categoryLevel === 0 ? 3 : 2;

			result = result === -1 ? result : result + offset;
		}

		return result;
	}

	isEmptyData(rawChartData, props) {
		const { showNonSourceable } = props;
		const {
			addressable: addressableData,
			nonAddressable: nonAddressableData,
		} = rawChartData;
		let data = showNonSourceable ? nonAddressableData : addressableData;

		return data.length === 0;
	}

	getHighlightChartItemIndex(categories) {
		const { pmiBreadcrumbs, chartType } = this.props;

		const allowNextLevelNavigation = this.allowNextLevelNavigation();

		if (!allowNextLevelNavigation) {
			let breadcrumbLastItemIndex = pmiBreadcrumbs.length - 1;

			if (pmiBreadcrumbs[breadcrumbLastItemIndex] === TAIL_SPEND) {
				breadcrumbLastItemIndex -= 1;
			}

			const breadcrumbLastItemName = pmiBreadcrumbs[breadcrumbLastItemIndex];

			let categoryIndex = categories.findIndex(
				categoryName => categoryName === breadcrumbLastItemName
			);

			if (categoryIndex !== -1 && chartType === CHART_TYPES.waterfall) {
				// reverse for waterfall
				categoryIndex = categories.length - categoryIndex - 1;
			}

			return categoryIndex;
		}

		return -1;
	}

	getIfDataContainsTailSpendBar(data) {
		return !!data.find(item => item.isTailSpend);
	}

	allowNextLevelNavigation() {
		const { pmiBreadcrumbs } = this.props;
		const selectedCategories = pmiBreadcrumbs.filter(
			name => name !== TAIL_SPEND
		);

		const maxCategoryLevel = this.getMaxCategoryLevel();

		return maxCategoryLevel > selectedCategories.length;
	}

	getMaxCategoryLevel() {
		const { metadata } = this.props;
		const dimensionsMetadata = (metadata ? metadata.dimensions : []) || [];
		const categoryDimensionsMetadataItem = dimensionsMetadata.find(
			({ name }) => name === 'category'
		);

		if (
			categoryDimensionsMetadataItem &&
			categoryDimensionsMetadataItem.levels
		) {
			return categoryDimensionsMetadataItem.levels; // added 1 more level for direct/indirect dimension
		}

		// only single level by default
		return 1;
	}

	getSpendType = () => {
		const { pmiBreadcrumbs } = this.props;
		let exploreHierarchy = [...pmiBreadcrumbs];
		exploreHierarchy = exploreHierarchy.filter(item => item !== TAIL_SPEND);

		return exploreHierarchy && exploreHierarchy.length > 0
			? exploreHierarchy.shift()
			: null;
	};

	handleViewTransactionsClick = index => {
		const {
			chartType,
			pmiBreadcrumbs,
			showExplorePage,
			showTailSpend,
		} = this.props;
		const item = this.getItemByIndex(index);
		const categoryLevel = getCategoryLevelFromBreadcrumbs(pmiBreadcrumbs);
		const options = {};

		let isTailSpend =
			!!pmiBreadcrumbs &&
			pmiBreadcrumbs.length > 0 &&
			pmiBreadcrumbs.includes(TAIL_SPEND);

		let itemName = null;
		let exploreHierarchy = [...pmiBreadcrumbs];

		if (
			chartType === CHART_TYPES.waterfall &&
			categoryLevel > 0 &&
			index === 0
		) {
			// ignore click on the first bar - show for current hierarchy
		} else if (item.isTailSpend) {
			isTailSpend = true;
		} else {
			exploreHierarchy = [...exploreHierarchy, item.name];
		}

		exploreHierarchy = exploreHierarchy.filter(item => item !== TAIL_SPEND);
		itemName = exploreHierarchy[exploreHierarchy.length - 1];

		const spendType =
			exploreHierarchy && exploreHierarchy.length > 0
				? exploreHierarchy.shift()
				: null;
		if (spendType) {
			options.spendType = spendType.toUpperCase();
		}

		if (showTailSpend) {
			options.isTailSpend = isTailSpend;
		}

		if (exploreHierarchy && exploreHierarchy.length > 0) {
			options.hierarchy = exploreHierarchy;
		} else {
			options.hierarchy = null;
		}

		showExplorePage(options, itemName);
	};

	handleChangeStatus = index => {
		const { companies } = this.props;
		const item = this.getItemByIndex(index);
		const categoryHierarchy = getHierarchyById(item.id).filter(
			item => ![TAIL_SPEND, DIRECT, INDIRECT].includes(item)
		);
		const data = {
			...(categoryHierarchy && categoryHierarchy.length > 0
				? { categoryHierarchy }
				: {}),
			extraFilters: { company: companies },
		};
		const spendType = this.getSpendType();

		if (spendType) {
			data.spendType = spendType.toUpperCase();
		} else {
			data.selectedDimensionName = item.name;
			data.selectedDimensionKey = 'spendType';
			data.selectedDimensionKeyTitle = 'spend type';
		}

		this.setState({ changeStatusModalData: data });
	};

	handleTopVendorsClick = index => {
		const { companies, showTailSpend, pmiBreadcrumbs } = this.props;
		const item = this.getItemByIndex(index);
		const { name, isTailSpend } = item;
		const categoryHierarchy = getHierarchyById(item.id).filter(
			item => !['tailSpend', DIRECT, INDIRECT].includes(item)
		);
		const getTailSpendOnly = pmiBreadcrumbs.includes(TAIL_SPEND) || isTailSpend;
		const data = {
			name,
			includeTailSpend: !showTailSpend || getTailSpendOnly,
			getTailSpendOnly,
			extraFilters: { company: companies },
			...(categoryHierarchy && categoryHierarchy.length > 0
				? { hierarchy: categoryHierarchy }
				: {}),
		};
		const spendType = this.getSpendType();

		// If spend type drilled or spend type item clicked (exclude tail spend)
		if (spendType || !isTailSpend) {
			const value = spendType || name;

			data.extraFilters = {
				...data.extraFilters,
				spendType: [SPEND_PARAM[value.toUpperCase()]],
			};
		}

		this.setState({ topVendorsModalData: data });
	};

	hideTopVendorsModal = () => {
		this.setState({
			topVendorsModalData: null,
		});
	};

	handleMoveToBin = index => {
		const { companies } = this.props;
		const item = this.getItemByIndex(index);
		const categoryHierarchy = getHierarchyById(item.id).filter(
			item => ![TAIL_SPEND, DIRECT, INDIRECT].includes(item)
		);
		const data = {
			...(categoryHierarchy && categoryHierarchy.length > 0
				? { categoryHierarchy }
				: {}),
			extraFilters: { company: companies },
		};
		const spendType = this.getSpendType();

		if (spendType) {
			data.spendType = spendType.toUpperCase();
		} else {
			data.selectedDimensionName = item.name;
			data.selectedDimensionKey = 'spendType';
			data.selectedDimensionKeyTitle = 'spend type';
		}

		this.setState({ moveToBinModalData: data });
	};

	handleItemClick = index => {
		const {
			chartType,
			pmiBreadcrumbs,
			setQueryStringStateAndPush,
			showNonSourceable,
		} = this.props;
		const {
			chartData: { highlightItemIndex },
		} = this.state;

		const item = this.getItemByIndex(index);
		const categoryLevel = getCategoryLevelFromBreadcrumbs(pmiBreadcrumbs);

		if (
			chartType === CHART_TYPES.waterfall &&
			categoryLevel === 0 &&
			index === 0
		) {
			// switch to addressable/non addressable
			setQueryStringStateAndPush({
				showNonSourceable: !showNonSourceable,
			});
		} else if (
			chartType === CHART_TYPES.waterfall &&
			categoryLevel === 0 &&
			index === 1
		) {
			return;
		} else if (
			chartType === CHART_TYPES.waterfall &&
			categoryLevel > 0 &&
			index === 0
		) {
			// go one level up
			const options = {
				pmiBreadcrumbs: [...pmiBreadcrumbs],
			};
			options.pmiBreadcrumbs.pop();

			setQueryStringStateAndPush(options);
		} else if (item.isGrouped) {
			setQueryStringStateAndPush({
				showAllCategories: true,
			});
		} else if (item.isTailSpend) {
			const options = {
				pmiBreadcrumbs: [...pmiBreadcrumbs, TAIL_SPEND],
			};

			setQueryStringStateAndPush(options);
		} else if (this.allowNextLevelNavigation()) {
			this.setState({ loading: true });

			const options = {
				pmiBreadcrumbs: [...pmiBreadcrumbs, item.name],
			};
			setQueryStringStateAndPush(options);
		} else {
			let updatedPmiBreadcrumbs;

			if (highlightItemIndex === index) {
				updatedPmiBreadcrumbs = this.getBreadcrumbsWithRemovedItem(
					pmiBreadcrumbs,
					item.name
				);
			} else {
				updatedPmiBreadcrumbs = this.getBreadcrumbsWithReplacedLastItem(
					pmiBreadcrumbs,
					item.name
				);
			}

			const options = {
				pmiBreadcrumbs: updatedPmiBreadcrumbs,
			};
			setQueryStringStateAndPush(options);
		}
	};

	getBreadcrumbsWithRemovedItem(pmiBreadcrumbs) {
		const result = this.getBreadcrumbsWithReplacedLastItem(
			pmiBreadcrumbs,
			null
		);

		return result.filter(x => x);
	}

	getBreadcrumbsWithReplacedLastItem(pmiBreadcrumbs, item) {
		let replaceIndex = pmiBreadcrumbs.length - 1;

		if (pmiBreadcrumbs[replaceIndex] === TAIL_SPEND) {
			replaceIndex -= 1;
		}

		const result = [...pmiBreadcrumbs];

		result[replaceIndex] = item;

		return result;
	}

	getItemByIndex = index => {
		let {
			chartType,
			showNonSourceable,
			pmiBreadcrumbs,
			companies,
			companiesList,
		} = this.props;
		const {
			rawChartData: {
				addressable: addressableData,
				nonAddressable: nonAddressableData,
			},
		} = this.state;

		let data = showNonSourceable ? nonAddressableData : addressableData;
		companies = getCompaniesWithDefaults(companies, companiesList);
		data = this.getDataFilteredBySelectedCompaniesValues(data, companies);

		if (chartType === CHART_TYPES.waterfall) {
			const categoryLevel = getCategoryLevelFromBreadcrumbs(pmiBreadcrumbs);

			// To the original items are added two or three items to the left:
			// for level 0 - sourceable and non-sourceable (plus hidden negative bar) -> 3
			// for level 1 and up - only total bar (plus hidden negative bar) -> 2
			const item = categoryLevel === 0 ? data[index - 3] : data[index - 2];
			return item;
		} else if (chartType === CHART_TYPES.bar) {
			const item = data[index];

			return item;
		}
	};

	handleSetTopChartRef = chartRef => {
		this.chartRef = chartRef;
	};

	handleSetChartMinMax = chartMinMax => {
		this.setState({ chartMinMax });
	};

	handleHighchartsExportToPng = chartRef => {
		chartRef = chartRef || this.chartRef;

		if (chartRef) {
			const chartName = 'Waterfall';
			const chart = chartRef.current.chart;

			chart.exportChartLocal(
				{
					filename: chartName,
				},
				{
					chart: {
						width: 1240,
						height: 500,
					},
				}
			);
		}
	};

	handleExportToXls = async () => {
		const { apiCacheTrigger } = this.props;
		const params = getHeroChartFileExportParamsFromProps(this.props);

		try {
			await memoizedApi.generateHeroChartDataFile(params, apiCacheTrigger);

			this.setState({ showDownloadNotification: true });
		} catch (e) {
			this.setState({ showDownloadNotification: true, exportToXlsError: true });
		}

		setTimeout(() => {
			if (!this.unmounted) {
				this.handleCloseNotification();
			}
		}, 5000);
	};

	handleResetFilters = () => {
		const { setQueryStringStateAndPush } = this.props;

		setQueryStringStateAndPush({
			customFilters: [],
			customFiltersValues: [],
			showNonSourceable: false,
			pmiBreadcrumbs: [],
			storedQueryId: undefined,
		});
	};

	handleCloseNotification = () => {
		this.setState({
			showDownloadNotification: false,
			exportToXlsError: false,
		});
	};

	componentWillUnmount() {
		this.unmounted = true;
	}

	render() {
		const {
			chartType,
			pmiBreadcrumbs,
			currencySymbol,
			isTailSpend,
		} = this.props;
		const {
			changeStatusModalData,
			topVendorsModalData,
			moveToBinModalData,
			chartData,
			chartMinMax,
			isLoading,
			showDownloadNotification,
			exportToXlsError,
		} = this.state;
		const { refreshPmiContent } = this.context;
		const allowShowAllCategories = true;
		const allowShowTailSpend = true;
		let hideIndex;

		if (isLoading) {
			return (
				<Wrapper>
					<LoadingContainer>
						<ApLoaderDefault />
					</LoadingContainer>
				</Wrapper>
			);
		}

		const showNoDataMsg = chartData.noData;
		const categoryLevel = getCategoryLevelFromBreadcrumbs(pmiBreadcrumbs);
		if (chartData.categories && chartType === CHART_TYPES.waterfall) {
			hideIndex =
				categoryLevel === 0
					? chartData.categories.length - 3
					: chartData.categories.length - 2;
		}

		return (
			<Wrapper>
				<FluidSection>
					<HeroChartOptionsContainer
						allowShowAllCategories={allowShowAllCategories}
						allowShowTailSpend={allowShowTailSpend}
						onPngExportClick={this.handleHighchartsExportToPng}
						onXlsExportClick={this.handleExportToXls}
						noData={showNoDataMsg}
					/>
					{showNoDataMsg ? (
						<NoDataWrapper>
							<NoData
								title="No data available for this combination of filters"
								msg="Please try to reset or change the filter options"
								onResetClick={this.handleResetFilters}
							/>
						</NoDataWrapper>
					) : (
						<HeroChart
							{...chartData}
							{...chartMinMax}
							isTailSpend={isTailSpend}
							hideIndex={hideIndex}
							onItemClick={this.handleItemClick}
							onChangeStatusClick={this.handleChangeStatus}
							onMoveToBinClick={this.handleMoveToBin}
							onViewTransactionsClick={this.handleViewTransactionsClick}
							onTopVendorsClick={this.handleTopVendorsClick}
							type={chartType}
							setChartRef={this.handleSetTopChartRef}
							setChartMinMax={this.handleSetChartMinMax}
							categoryLevel={categoryLevel}
							currencySymbol={currencySymbol}
						/>
					)}
				</FluidSection>
				{showDownloadNotification && (
					<DownloadProgressBar
						error={exportToXlsError}
						failMessage="Error generating file"
						successMessage="Generating file... You will receive a notification when the file has been generated"
						onClose={this.handleCloseNotification}
					/>
				)}
				{topVendorsModalData && (
					<TopVendorsModal
						isPmi
						onClose={this.hideTopVendorsModal}
						{...topVendorsModalData}
					/>
				)}
				{changeStatusModalData && (
					<ChangeTypeModal
						isOpen={changeStatusModalData}
						isHarmonized
						onClose={() => {
							this.setState({ changeStatusModalData: null });
						}}
						onSubmit={refreshPmiContent}
						isPmi
						{...changeStatusModalData}
					/>
				)}
				{moveToBinModalData && (
					<MoveToBinModal
						isOpen={moveToBinModalData}
						isHarmonized
						onClose={() => {
							this.setState({ moveToBinModalData: null });
						}}
						onSubmit={refreshPmiContent}
						isPmi
						{...moveToBinModalData}
					/>
				)}
			</Wrapper>
		);
	}
}
